/*
  Story Carousel — infinite coverflow.

  Every slide sits in the same grid cell (so all cards share the tallest card's
  height) and gets a data-pos: center | prev | next | hidden-prev | hidden-next.
  CSS turns each position into a transform. Looping is modular, so no clones are
  needed: when a slide wraps from one side to the other it is first parked,
  without transition, on the side it is entering from, then animated in.
*/
if (!customElements.get('story-carousel')) {
  class StoryCarousel extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-story-stage]');
      this.slides = [...this.querySelectorAll('[data-story-slide]')];
      this.total = this.slides.length;
      this.current = 0;
      this.touchStartX = 0;

      this.render(false);
      this.initPillarGifs();
      if (this.total < 2) return;

      // Enable transitions only after the first paint so the initial layout doesn't animate.
      requestAnimationFrame(() => requestAnimationFrame(() => this.stage.classList.add('is-ready')));

      this.querySelector('[data-story-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-story-next]')?.addEventListener('click', () => this.go(1));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.stage.addEventListener('click', this.onStageClick.bind(this));
      this.stage.addEventListener(
        'touchstart',
        (e) => {
          this.touchStartX = e.changedTouches[0].screenX;
          this.touchStartY = e.changedTouches[0].screenY;
        },
        { passive: true }
      );
      // Non-passive so a sideways swipe can stop the page from scrolling.
      this.stage.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      this.stage.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      this.stage.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

      // Theme editor: selecting a card (or one of its pillars/facts) brings it to the front.
      this.addEventListener('shopify:block:select', (e) => {
        const slide = e.target.closest('[data-story-slide]');
        const index = this.slides.indexOf(slide);
        if (index > -1) this.goTo(index);
      });
    }

    offsetFor(index) {
      let offset = (((index - this.current) % this.total) + this.total) % this.total;
      if (offset > this.total / 2) offset -= this.total;
      return offset;
    }

    positionFor(offset) {
      if (offset === 0) return 'center';
      if (offset === -1) return 'prev';
      if (offset === 1) return 'next';
      return offset < 0 ? 'hidden-prev' : 'hidden-next';
    }

    render(animate) {
      this.slides.forEach((slide, index) => {
        const offset = this.offsetFor(index);
        const lastOffset = slide._storyOffset;

        // Wrapping slide: park it off-stage on the side it enters from, then animate in.
        if (animate && lastOffset !== undefined && Math.abs(offset - lastOffset) > 1) {
          slide.style.transition = 'none';
          slide.dataset.pos = offset > 0 ? 'hidden-next' : 'hidden-prev';
          void slide.offsetWidth;
          slide.style.transition = '';
        }

        slide._storyOffset = offset;
        slide.dataset.pos = this.positionFor(offset);

        const active = offset === 0;
        if (active && slide.dataset.accent) this.dataset.activeAccent = slide.dataset.accent;
        if (animate && active && lastOffset !== 0) this.replayGifs(slide);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
    }

    /*
      Pillar icons are GIFs exported to play once. A GIF can't be rewound, so to
      replay it we fetch it once as a blob and point the <img> at a fresh object
      URL, which the browser decodes from frame 1 (no extra network request).
      Replays: when the card first scrolls into view, when it returns to the
      center of the carousel, and on hover of each pillar.
      With reduced motion the first frame is frozen onto a canvas instead.
    */
    initPillarGifs() {
      this.gifs = [...this.querySelectorAll('[data-pillar-gif]')];
      if (!this.gifs.length) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.gifs.forEach((img) => this.freezeGif(img));
        return;
      }

      this.gifs.forEach((img) => {
        img.dataset.gifSrc = img.src;
        img.closest('[data-pillar]')?.addEventListener('mouseenter', () => this.replayGif(img));
      });

      const slide = this.gifs[0].closest('[data-story-slide]');
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          if (slide?.dataset.pos === 'center') this.replayGifs(slide);
        },
        { threshold: 0.4 }
      );
      if (slide) observer.observe(slide);
    }

    replayGifs(scope) {
      if (!this.gifs?.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      scope.querySelectorAll('[data-pillar-gif]').forEach((img, i) => {
        // Small stagger so the five icons don't fire as one block.
        setTimeout(() => this.replayGif(img), i * 90);
      });
    }

    async replayGif(img) {
      if (!img.dataset.gifSrc) return;
      try {
        img._gifBlob = img._gifBlob || (await (await fetch(img.dataset.gifSrc)).blob());
        const previous = img._gifUrl;
        img._gifUrl = URL.createObjectURL(img._gifBlob);
        img.src = img._gifUrl;
        if (previous) URL.revokeObjectURL(previous);
      } catch (error) {
        // Network/CORS failure: leave the icon as it is.
      }
    }

    freezeGif(img) {
      const draw = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.setAttribute('aria-hidden', 'true');
        try {
          canvas.getContext('2d').drawImage(img, 0, 0);
          img.replaceWith(canvas);
        } catch (error) {
          // Tainted canvas or decode failure: keep the <img>.
        }
      };
      img.loading = 'eager';
      img.complete && img.naturalWidth ? draw() : img.addEventListener('load', draw, { once: true });
    }

    go(step) {
      this.current = (this.current + step + this.total) % this.total;
      this.render(true);
    }

    goTo(index) {
      if (index === this.current) return;
      this.current = index;
      this.render(true);
    }

    onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(1);
      }
    }

    // Side cards are inert, so clicks on them land on the stage: use the click side.
    onStageClick(e) {
      if (e.target.closest('[data-pos="center"]')) return;
      const rect = this.stage.getBoundingClientRect();
      this.go(e.clientX < rect.left + rect.width / 2 ? -1 : 1);
    }

    /*
      Sideways gestures belong to the carousel. A horizontal swipe always
      carries a few px of vertical drift; left alone, that drift scrolled the
      page and made the sticky header drop in. Mostly-horizontal gestures are
      therefore kept from reaching the page (and Lenis, which listens on
      window); vertical ones pass through untouched.
    */
    onTouchMove(e) {
      if (!e.cancelable) return;
      const touch = e.changedTouches[0];
      const dx = Math.abs(touch.screenX - this.touchStartX);
      const dy = Math.abs(touch.screenY - this.touchStartY);
      if (dx > 8 && dx > dy) e.preventDefault();
    }

    onTouchEnd(e) {
      const diff = this.touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > 50) this.go(diff > 0 ? 1 : -1);
    }

    // Trackpad: one swipe = one card. The swipe's inertia keeps firing wheel
    // events, so the carousel stays locked until they have been quiet briefly.
    onWheel(e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      e.stopPropagation();

      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => {
        this.wheelLocked = false;
        this.wheelDelta = 0;
      }, 180);

      if (this.wheelLocked) return;
      this.wheelDelta = (this.wheelDelta || 0) + e.deltaX;
      if (Math.abs(this.wheelDelta) > 40) {
        this.go(this.wheelDelta > 0 ? 1 : -1);
        this.wheelLocked = true;
      }
    }
  }

  customElements.define('story-carousel', StoryCarousel);
}
